import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { SortDirection } from '@cast/design-system';

import { apiClient } from '@cast/infrastructure-api';

const NAMESPACES_DATA_TRANSFER_SUMMARY_REPORT_QUERY_KEY =
  'namespaces-data-transfer-summary-report';

const getNamespacesDataTransferSummaryQueryKey = ({
  startTime,
  endTime,
  clusterId,
  sortBy,
  sortDirection,
  pageLimit,
}: Partial<Props> = {}) =>
  [
    NAMESPACES_DATA_TRANSFER_SUMMARY_REPORT_QUERY_KEY,
    ...(startTime !== undefined
      ? [startTime, endTime, clusterId, sortBy, sortDirection, pageLimit]
      : []),
  ] as const;

type Props = {
  startTime: string;
  endTime: string;
  clusterId: string;
  pageLimit?: number;
  sortBy?: string;
  sortDirection?: SortDirection;
  enabled?: boolean;
};

const getNamespacesDataTransferSummaryQueryOptions = ({
  startTime,
  endTime,
  clusterId,
  sortBy,
  sortDirection,
  pageLimit = 50,
  enabled = true,
}: Props) =>
  infiniteQueryOptions({
    queryKey: getNamespacesDataTransferSummaryQueryKey({
      startTime,
      endTime,
      clusterId,
      sortBy,
      sortDirection,
      pageLimit,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } =
        await apiClient.costReport.getNamespacesDataTransferCostSummary({
          startTime,
          endTime,
          clusterId,
          pageLimit: String(pageLimit),
          pageCursor: pageParam,
          sortField: sortBy,
          sortOrder: sortDirection,
        });

      return data;
    },
    enabled,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor || undefined;
    },
  });

export const useNamespacesDataTransferSummaryQuery = (props: Props) => {
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery(getNamespacesDataTransferSummaryQueryOptions(props));

  return {
    isLoading,
    data: flatMap(data?.pages, (page) => page?.items ?? []),
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    count: Number(data?.pages[0].count ?? 0),
    noDataReason: data?.pages[0].noDataReason ?? '',
  };
};
